import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

export interface SearchQuery {
  query: string;
  timestamp: number;
  resultCount: number;
}

@Injectable({
  providedIn: "root",
})
export class SearchService {
  private readonly STORAGE_KEY = "treeslo_search_history";
  private readonly MAX_HISTORY = 5;

  private recentSearchesSubject = new BehaviorSubject<SearchQuery[]>([]);
  public recentSearches$: Observable<SearchQuery[]> =
    this.recentSearchesSubject.asObservable();

  private openSearchSubject = new Subject<void>();
  public openSearch$ = this.openSearchSubject.asObservable();

  constructor() {
    this.loadSearchHistory();
    this.initializeKeyboardShortcut();
  }

  /**
   * Initialize Ctrl+K keyboard shortcut to open search
   */
  private initializeKeyboardShortcut(): void {
    document.addEventListener("keydown", (event: KeyboardEvent) => {
      // Check for Ctrl+K (Windows/Linux) or Cmd+K (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        this.openSearch();
      }
    });
  }

  /**
   * Trigger the open search event
   */
  openSearch(): void {
    this.openSearchSubject.next();
  }

  /**
   * Add a search query to history with result count
   */
  addSearch(query: string, resultCount: number = 0): void {
    if (!query || query.trim() === "") {
      return;
    }

    const trimmedQuery = query.trim();
    let history = this.recentSearchesSubject.value;

    // Remove if already exists to avoid duplicates
    history = history.filter((item) => item.query !== trimmedQuery);

    // Add new search at the beginning
    const newSearch: SearchQuery = {
      query: trimmedQuery,
      timestamp: Date.now(),
      resultCount: resultCount,
    };

    history = [newSearch, ...history].slice(0, this.MAX_HISTORY);
    this.recentSearchesSubject.next(history);
    this.saveSearchHistory(history);
  }

  /**
   * Get all recent searches
   */
  getRecentSearches(): SearchQuery[] {
    return this.recentSearchesSubject.value;
  }

  /**
   * Remove a specific search from history
   */
  removeSearch(query: string): void {
    const history = this.recentSearchesSubject.value.filter(
      (item) => item.query !== query,
    );
    this.recentSearchesSubject.next(history);
    this.saveSearchHistory(history);
  }

  /**
   * Clear all search history
   */
  clearHistory(): void {
    this.recentSearchesSubject.next([]);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Load search history from localStorage
   */
  private loadSearchHistory(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const history: SearchQuery[] = JSON.parse(stored);
        this.recentSearchesSubject.next(history);
      }
    } catch (error) {
      console.error("Error loading search history:", error);
      this.recentSearchesSubject.next([]);
    }
  }

  /**
   * Save search history to localStorage
   */
  private saveSearchHistory(history: SearchQuery[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.error("Error saving search history:", error);
    }
  }
}
